import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import DeleteButton from "../common/DeleteButton";
import ThreeDot from "../common/ThreeDot";
import Swal from "sweetalert2";
import {
  deleteSubCategory,
  getAllSubCategories,
} from "../../services/subcategory.service";
import { useSelector } from "react-redux";
import EditButton from "../common/EditButton";
import ViewDetailsButton from "../common/ViewDetailsButton";
import { getAllSubCategoriesGraphQL } from "../../services/graphql.service";
import { debounce } from "lodash";

export default function SubCategories() {
  const { token } = useSelector((state) => state?.auth);
  const [subCategories, setSubCategories] = useState([]);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [totalEntries, setTotalEntries] = useState(null);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const backPage = parseInt(queryParams.get("page"));
  const backSearch = queryParams.get("search");
  const [currentPage, setCurrentPage] = useState(backPage || 1);
  const [search, setSearch] = useState(backSearch || "");
  const [debouncedSearch, setDebouncedSearch] = useState(backSearch || "");

  // Fetch Subcategories
  const fetchSubCategories = async (page = currentPage) => {
    try {
      const params = { page };
      const res = await getAllSubCategories(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        setSubCategories(res?.data?.results);
        setNext(res?.data?.next);
        setPrevious(res?.data?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.count || null);
        setTotalPages(Math.ceil((res?.data?.count || 0) / itemsPerPage));
        console.log("Subcategories fetched successfully:", res.data);
      } else {
        Swal.fire(
          "Error",
          res?.data?.message ||
            "Failed to fetch subcategories. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchSubCategoriesGraphQL = async (page = 1, search) => {
    console.log("Fetching SubCategories:", { search, page });

    try {
      const res = await getAllSubCategoriesGraphQL(token, page, search);

      if (res && res.status >= 200 && res.status < 300) {
        console.log("SubCategories fetched successfully:", res.data);

        setSubCategories(res?.data?.data?.search_subcategories?.results);
        setNext(res?.data?.data?.search_subcategories?.next);
        setPrevious(res?.data?.data?.search_subcategories?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.data?.search_subcategories?.count || null);
        setTotalPages(
          Math.ceil(
            (res?.data?.data?.search_subcategories?.count || 0) / itemsPerPage
          )
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.message ||
            "Failed to fetch Subcategories. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching Subcategories:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearchHandler(value);
  };

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    if (debouncedSearch) {
      fetchSubCategoriesGraphQL(currentPage, debouncedSearch);
    } else {
      fetchSubCategories(currentPage);
    }
  }, [debouncedSearch, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  // Handle Subcategory Deletion
  const handleDelete = async (subCategoryName, subCategoryId) => {
    try {
      const res = await deleteSubCategory(token, subCategoryId);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire(
          "Deleted!",
          `${subCategoryName} subcategory has been deleted successfully.`,
          "success"
        ).then(() => {
          const remainingItems = subCategories.length - 1;

          if (remainingItems === 0 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
            if (search) {
              fetchSubCategoriesGraphQL(currentPage - 1, search);
            } else {
              fetchSubCategories(currentPage - 1);
            }
          } else {
            if (search) {
              fetchSubCategoriesGraphQL(currentPage, search);
            } else {
              fetchSubCategories(currentPage);
            }
          }
        });
      } else {
        Swal.fire(
          "Error",
          res?.data?.detail ||
            "Failed to delete the subcategory. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during subcategory deletion:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  return (
    <div className="content-body">
      {/* <!-- row --> */}
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder="Search here"
              onChange={(e) => handleSearchChange(e)}
            />
            <div className="input-group-append">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div>
          <div style={{ zIndex: 2 }}>
            <Link to="/sub-categories/add">
              <button className="btn btn-primary px-3 py-2 add-btn sticky-btn">
                Add
                <img
                  className="plus-sign ms-2"
                  src="/assets/images/kidskart/icon/add.png"
                />
              </button>
            </Link>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-12 col-xxl-12 col-lg-12 col-sm-12">
            <div className="card">
              <div className="card-body  p-0 ">
                {subCategories && subCategories.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table order-request2">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Image</th>
                          <th>Main Category</th>
                          <th>Sub Category</th>
                          <th>Created On</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {subCategories.map((item, i) => (
                          <tr key={item.sub_category_id}>
                            <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                            <td>
                              <div className="media media2 align-items-center">
                                <img
                                  className="img-fluid"
                                  width="75"
                                  src={
                                    item.sub_category_image_url || item.image
                                  } // Ensure fallback
                                  alt={item.sub_category_name}
                                />
                              </div>
                            </td>
                            <td>
                              {item.main_category_name ||
                                item.main_category?.category_name}
                            </td>
                            <td>{item.sub_category_name}</td>
                            <td>
                              {new Date(item.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="dropdown ms-auto c-pointer">
                                  <ThreeDot />
                                  <div className="dropdown-menu dropdown-menu-end">
                                    <EditButton
                                      to={`/sub-categories/update/`}
                                      id={item.sub_category_id}
                                      page={currentPage}
                                      search={search}
                                    />

                                    <DeleteButton
                                      name={`${item.sub_category_name} Subcategory`}
                                      onDelete={() =>
                                        handleDelete(
                                          item.sub_category_name,
                                          item.sub_category_id
                                        )
                                      }
                                    />

                                    <ViewDetailsButton
                                      to={`/sub-categories/details/`}
                                      id={item.sub_category_id}
                                      page={currentPage}
                                      search={search}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {subCategories.length < 3 ? (
                          <tr style={{ height: "205px" }}></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="my-5 py-5 text-center fs-4 fw-bold">
                    Please Add Subcategories
                  </div>
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {totalEntries && totalEntries > 10 && (
              <div className="dataTables_wrapper no-footer">
                <div className="dataTables_info">
                  Showing {startEntry} to {endEntry} of {totalEntries} entries
                </div>
                <div className="dataTables_paginate paging_simple_numbers">
                  <button
                    className={`btn btn-primary me-2 ${
                      !previous && "disabled"
                    }`}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    {(() => {
                      const maxVisible = 7;
                      const pages = Array.from(
                        { length: totalPages },
                        (_, i) => i + 1
                      );
                      let start = Math.max(
                        currentPage - Math.floor(maxVisible / 2),
                        1
                      );
                      let end = start + maxVisible - 1;

                      if (end > totalPages) {
                        end = totalPages;
                        start = Math.max(end - maxVisible + 1, 1);
                      }

                      return pages.slice(start - 1, end).map((page) => (
                        <button
                          key={page}
                          className={`btn btn-sm mx-1 ${
                            currentPage === page
                              ? "btn-dark"
                              : "btn-outline-dark"
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                  </span>
                  <button
                    className={`btn btn-primary ms-2 ${!next && "disabled"}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
